import { useEffect, useRef, useState } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { checkoutStorage } from '@/lib/ecommerce/checkoutStorage'
import { getShippingOption, shippingRowValue } from '@/lib/ecommerce/shippingOptions'
import type { OrderConfirmationPagePayload } from '@/types/ecommerce'

/*
  Order Confirmation (Teamwork 26087641) + post-order signup
  (Teamwork 26137500). The confirmation.liquid wrapper pre-fetches
  the order, order items, and the addon-style signup form HTML so the
  page renders on first paint with no extra client-side round-trip.

  - The autoresponder + workflow notification emails are triggered
    server-side by callback_init → callback_email_notifications when
    the payment succeeds; nothing for React to do here.
  - The signup form is rendered server-side via include_form so it
    inherits the addon's `resource: User` form pipeline. React injects
    it via dangerouslySetInnerHTML below the order details.

  Styling lives in src/styles/pages/order-confirmation.css.
*/

function rehydrateScripts(container: HTMLElement) {
  const scripts = Array.from(container.querySelectorAll('script'))
  for (const old of scripts) {
    const next = document.createElement('script')
    for (const attr of Array.from(old.attributes)) {
      next.setAttribute(attr.name, attr.value)
    }
    if (old.textContent) next.textContent = old.textContent
    old.parentNode?.replaceChild(next, old)
  }
}

function formatMoney(amount: number | null | undefined): string {
  return `$${(amount ?? 0).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function DetailBlock({
  heading,
  children,
}: {
  heading: string
  children: React.ReactNode
}) {
  return (
    <div className="mig-confirmation-detail-block">
      <h3 className="mig-confirmation-detail-heading">{heading}</h3>
      <div className="mig-confirmation-detail-lines">{children}</div>
    </div>
  )
}

function DetailLine({ children }: { children: React.ReactNode }) {
  return <p className="mig-confirmation-detail-line">{children}</p>
}

function SummaryRow({
  label,
  value,
  bold,
}: {
  label: string
  value: string
  bold?: boolean
}) {
  return (
    <div className="mig-confirmation-summary-row" data-bold={!!bold}>
      <span>{label}</span>
      <span className="mig-confirmation-summary-row-value">{value}</span>
    </div>
  )
}

export default function OrderConfirmation() {
  const payload = usePageData<OrderConfirmationPagePayload>() ?? {
    order: null,
    order_items: [],
    order_uuid: '',
    is_authenticated: false,
  }
  const order = payload.order
  const items = payload.order_items ?? []
  const hasOrder = !!order && !!order.uuid
  const signupFormHtml = payload.signup_form_html ?? ''
  const accountCreated = payload.account_status === 'created'
  const alreadyRegistered = !!payload.already_registered
  const contactEmail = payload.order_contact_email ?? order?.order_contact_email ?? ''

  // UAT-36: the chosen shipping option is no longer persisted on the order
  // (it isn't a column on the locked order schema — that was the cause of
  // the payment-page crash). Capture it from the checkout sessionStorage
  // mirror on first render — before the clearAll() effect below wipes it —
  // so we can still show the exact method + next-step. On a later refresh
  // (mirror already cleared) this is '' and the summary falls back to the
  // persisted total_shipping_amount.
  const [shippingOptionKey] = useState<string>(
    () => checkoutStorage.readShipping()?.shipping_option ?? '',
  )

  const formMountRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (formMountRef.current && signupFormHtml) {
      rehydrateScripts(formMountRef.current)
    }
  }, [signupFormHtml])

  useEffect(() => {
    if (hasOrder) {
      checkoutStorage.clearAll()
    }
  }, [hasOrder])

  const shippingAddress = [
    order?.shipping_address_1,
    order?.shipping_address_2,
    order?.shipping_address_3,
    order?.shipping_suburb,
    order?.shipping_state,
    order?.shipping_postcode,
    order?.shipping_country,
  ]
    .filter(Boolean)
    .join(', ')

  const billingAddress = [
    order?.billing_address_1,
    order?.billing_address_2,
    order?.billing_address_3,
    order?.billing_suburb,
    order?.billing_state,
    order?.billing_postcode,
    order?.billing_country,
  ]
    .filter(Boolean)
    .join(', ')

  const payment = order?.payment ?? null
  const paddedExpiryMonth =
    payment?.card_expiry_month && payment.card_expiry_month.length < 2
      ? `0${payment.card_expiry_month}`
      : payment?.card_expiry_month ?? ''

  return (
    <section className="mig-confirmation-section">
      <h1 className="mig-confirmation-page-title">Thank you</h1>

      {!hasOrder && (
        <p className="mig-confirmation-missing">
          We couldn't find your order. <a href="/marketplace">Back to the Marketplace</a>.
        </p>
      )}

      {hasOrder && (
        <div className="mig-confirmation-body">
          {/* Confirmation summary */}
          <p className="mig-confirmation-intro">
            Your order has been received with order number{' '}
            <strong>{order?.id ?? payload.order_uuid}</strong>. A
            confirmation email is on its way to{' '}
            <strong>{order?.order_contact_email ?? 'you'}</strong>.
          </p>

          <div className="mig-confirmation-grid">
            <div className="mig-confirmation-main">
              <h2 className="mig-confirmation-section-heading">Order details</h2>

              {(() => {
                const fullName = (first?: string | null, last?: string | null) =>
                  [first, last].filter(Boolean).join(' ').trim()
                const fullPhone = (cc?: string | null, num?: string | null) =>
                  num ? `${cc ? `(${cc}) ` : ''}${num}` : ''
                const orderName = fullName(order?.order_contact_first_name, order?.order_contact_last_name)
                const orderPhone = fullPhone(order?.order_contact_phone_country_code, order?.order_contact_phone_number)
                const shipName = fullName(order?.shipping_contact_first_name, order?.shipping_contact_last_name)
                const shipPhone = fullPhone(order?.shipping_contact_phone_country_code, order?.shipping_contact_phone_number)
                const billName = fullName(order?.billing_contact_first_name, order?.billing_contact_last_name)
                const billPhone = fullPhone(order?.billing_contact_phone_country_code, order?.billing_contact_phone_number)
                return (
                  <div className="mig-confirmation-details">
                    <div className="mig-confirmation-col">
                      <DetailBlock heading="Order contact">
                        {order?.order_company_name && <DetailLine>{order.order_company_name}</DetailLine>}
                        {orderName && <DetailLine>{orderName}</DetailLine>}
                        {order?.order_contact_email && <DetailLine>{order.order_contact_email}</DetailLine>}
                        {orderPhone && <DetailLine>{orderPhone}</DetailLine>}
                      </DetailBlock>

                      {(() => {
                        // UAT-36: per-option shipping method line + next-step
                        // copy. The option key comes from the sessionStorage
                        // mirror (shippingOptionKey above) since it's no longer
                        // persisted on the order. Address suppressed for pickups.
                        const shipOption = getShippingOption(shippingOptionKey)
                        const shipIsPickup = shipOption?.isPickup ?? false
                        const methodLabel = shipOption?.summaryLabel ?? ''
                        return (
                          <>
                            {(methodLabel || shipOption?.nextStep) && (
                              <DetailBlock heading="Shipping method">
                                {methodLabel && <DetailLine>{methodLabel}</DetailLine>}
                                {shipOption?.nextStep && <DetailLine>{shipOption.nextStep}</DetailLine>}
                              </DetailBlock>
                            )}

                            <DetailBlock heading="Shipping details">
                              {order?.shipping_company_name && <DetailLine>{order.shipping_company_name}</DetailLine>}
                              {shipName && <DetailLine>{shipName}</DetailLine>}
                              {order?.shipping_contact_email && <DetailLine>{order.shipping_contact_email}</DetailLine>}
                              {shipPhone && <DetailLine>{shipPhone}</DetailLine>}
                              {!shipIsPickup && shippingAddress && <DetailLine>{shippingAddress}</DetailLine>}
                            </DetailBlock>
                          </>
                        )
                      })()}

                      {order?.shipping_instructions && (
                        <DetailBlock heading="Shipping instructions">
                          <DetailLine>{order.shipping_instructions}</DetailLine>
                        </DetailBlock>
                      )}
                    </div>

                    <div className="mig-confirmation-col">
                      <DetailBlock heading="Billing details">
                        {order?.billing_company_name && <DetailLine>{order.billing_company_name}</DetailLine>}
                        {billName && <DetailLine>{billName}</DetailLine>}
                        {order?.billing_contact_email && <DetailLine>{order.billing_contact_email}</DetailLine>}
                        {billPhone && <DetailLine>{billPhone}</DetailLine>}
                        {billingAddress && <DetailLine>{billingAddress}</DetailLine>}
                      </DetailBlock>

                      {payment && (
                        <DetailBlock heading="Payment details">
                          <DetailLine>
                            {(payment.card_brand ?? '').toUpperCase()} **** **** ****{' '}
                            {payment.card_last_four_digits}
                          </DetailLine>
                          {(paddedExpiryMonth || payment.card_expiry_year) && (
                            <DetailLine>
                              {paddedExpiryMonth} / {payment.card_expiry_year}
                            </DetailLine>
                          )}
                        </DetailBlock>
                      )}
                    </div>
                  </div>
                )
              })()}
            </div>

            <aside className="mig-confirmation-summary" aria-label="Order summary">
              <h2 className="mig-confirmation-summary-title">Order summary</h2>

              {items.length > 0 && (
                <ul className="mig-confirmation-items">
                  {items.map((it) => {
                    const linePrice =
                      typeof it.line_item_total === 'number' && it.line_item_total > 0
                        ? it.line_item_total
                        : (it.unit_price ?? 0) * (it.quantity ?? 1)
                    // Prefer the snapshot stored on the order_item itself
                    // (set during checkout if the addon's create_order_items
                    // partial wrote one); fall back to the joined product's
                    // product_image when the snapshot is blank.
                    const imageUrl =
                      it.order_item_image?.url ??
                      it.product?.product_image?.url ??
                      null
                    return (
                      <li key={it.uuid ?? it.id} className="mig-confirmation-item">
                        <div
                          className="mig-confirmation-item-thumb"
                          style={imageUrl ? ({ '--bg-image': `url("${imageUrl}")` } as React.CSSProperties) : undefined}
                          aria-hidden="true"
                        />
                        <span className="mig-confirmation-item-name">
                          {it.order_item_name ?? 'Artwork'}
                        </span>
                        {/* Quantity badge intentionally omitted — every
                            MIG artwork is one-of-a-kind (qty = 1), so the
                            "1x" indicator was redundant noise. */}
                        <span className="mig-confirmation-item-price">
                          {formatMoney(linePrice)}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              )}

              <hr className="mig-confirmation-hr" />

              <div className="mig-confirmation-charges">
                <SummaryRow label="Subtotal" value={formatMoney(order?.subtotal_amount)} />
                <SummaryRow label="Tax" value={formatMoney(order?.total_tax_amount ?? order?.tax_amount)} />
                {shippingOptionKey ? (
                  <SummaryRow label="Shipping" value={shippingRowValue(shippingOptionKey)} />
                ) : order?.total_shipping_amount ? (
                  <SummaryRow label="Shipping" value={formatMoney(order.total_shipping_amount)} />
                ) : null}
              </div>

              <hr className="mig-confirmation-hr mig-confirmation-hr--total" />

              <SummaryRow
                label="Total"
                value={formatMoney(order?.order_total ?? order?.total_amount)}
                bold
              />
            </aside>
          </div>

          {/* Post-order signup (Teamwork 26137500) */}
          {accountCreated && (
            <div className="mig-confirmation-banner">
              <strong>Account created.</strong> Next time you check
              out you can sign in with this email and your details will be pre-filled. You can also
              reset your password from the <a href="/forgot-password">forgot password page</a> at any time.
            </div>
          )}

          {!accountCreated && alreadyRegistered && (
            <div className="mig-confirmation-banner mig-confirmation-banner--registered">
              <strong>Welcome back.</strong> An account already
              exists for <strong>{contactEmail}</strong>.{' '}
              <a href="/sign-in">Sign in</a>{' '}
              to view your order history, or reset your password from the{' '}
              <a href="/forgot-password">forgot password page</a>
              .
            </div>
          )}

          {!accountCreated && !alreadyRegistered && signupFormHtml && (
            <div className="mig-customer-signup-host">
              <h2 className="mig-customer-signup-heading">Create an account</h2>
              <p className="mig-customer-signup-subtitle">
                Create an account to save your details for next time.
              </p>
              <div
                ref={formMountRef}
                dangerouslySetInnerHTML={{ __html: signupFormHtml }}
              />
            </div>
          )}

        </div>
      )}
    </section>
  )
}
